import React, { useEffect, useState } from 'react'

import { isAuthenticated } from '@/shared/functions/Auth/auth.functions'
import { Alert, Box, Card, CardContent, Typography } from '@mui/material'
import { FormikProps } from 'formik'
import * as Yup from 'yup'

import DiscountCode from '../../../../shared/components/DiscountCode/DiscountCode.component'
import Form from '../../../../shared/components/Form/Form.component'

import { getProfile } from '../../../../redux/states/profile/getProfile/getProfile.slice'
import { GetProfileData, GetProfileRequestPayload } from '../../../../redux/states/profile/getProfile/getProfile.types'
import { FormSection } from '../../../../shared/components/Form/Form.types'
import { useAppDispatch } from '../../../../shared/hooks/redux.hooks'
import {
  HARDCODED_SHOPPER_DELIVERY_CITY_OPTIONS,
  HARDCODED_SHOPPER_DELIVERY_COUNTRY_OPTIONS,
  HARDCODED_SHOPPER_DELIVERY_DESTINATION
} from './DeliveryDetails.dictionary'
import styles from './DeliveryDetails.styles'
import { DeliveryDetailsProps, DeliveryDetailsValues } from './DeliveryDetails.types'

export const useProfileData = () => {
  const [profileData, setProfileData] = useState<GetProfileData | null>(null)
  const dispatch = useAppDispatch()

  useEffect(() => {
    const payload: GetProfileRequestPayload = {
      request: {},
      onSuccess: (data: GetProfileData) => {
        setProfileData(data)
      }
    }

    if (isAuthenticated()) {
      dispatch(getProfile(payload))
    }
  }, [dispatch])

  return profileData
}

const DeliveryDetails: React.FC<DeliveryDetailsProps> = (props) => {
  const {
    orderCountries,
    onFormChange,
    flightItinerary,
    discountCode,
    onDiscountCodeChange,
    showDiscountCode,
    orderTotal
  } = props
  const profileData = useProfileData()

  const countries = Array.from(new Set(orderCountries.map((country) => country.name))).join(', ')

  const initialValues: DeliveryDetailsValues = {
    firstName: profileData?.firstName || '',
    lastName: profileData?.lastName || '',
    phoneNumber: profileData?.mobileNumber || '',

    deliveringFrom: {
      countries: countries
    },

    deliveryTo: {
      country: HARDCODED_SHOPPER_DELIVERY_DESTINATION.countryIataCode,
      city: HARDCODED_SHOPPER_DELIVERY_DESTINATION.cityIataCode
    },

    timePeriod: flightItinerary ? 'Anytime' : null,
    deliveryInstructions: '',
    discountCode: discountCode || ''
  }

  const validationSchema = Yup.object({
    firstName: Yup.string().required('Required'),
    lastName: Yup.string().required('Required'),
    phoneNumber: Yup.string().required('Required'),

    deliveringFrom: Yup.object({
      countries: Yup.string().required('Required')
    }),

    deliveryTo: Yup.object({
      country: Yup.string().required('Required'),
      city: Yup.string().required('Required')
    }),

    timePeriod: Yup.string().required('Required'),
    deliveryInstructions: Yup.string(),
    discountCode: Yup.string()
  })

  let formSections: FormSection[] = [
    {
      title: 'Personal Information',
      alignment: 'left',
      fields: [
        {
          name: 'firstName',
          label: 'First Name',
          type: 'text',
          grid: { xs: 12, sm: 6 },
          required: true,
          fullWidth: true
        },
        {
          name: 'lastName',
          label: 'Last Name',
          type: 'text',
          grid: { xs: 12, sm: 6 },
          required: true,
          fullWidth: true
        },
        {
          name: 'phoneNumber',
          label: 'Phone Number',
          type: 'tel',
          grid: { xs: 12, sm: 6 },
          required: true,
          fullWidth: true
        }
      ]
    },

    {
      title: 'Delivering From',
      alignment: 'left',
      fields: [
        {
          name: 'deliveringFrom.countries',
          label: 'Country',
          type: 'text',
          grid: { xs: 12, sm: 12 },
          required: true,
          fullWidth: true,
          readonly: true
        }
      ]
    },

    {
      title: 'Delivery To',
      alignment: 'left',
      fields: [
        {
          name: 'deliveryTo.country',
          label: 'Country',
          type: 'select',
          options: HARDCODED_SHOPPER_DELIVERY_COUNTRY_OPTIONS,
          grid: { xs: 12, sm: 6 },
          required: true,
          fullWidth: true,
          readonly: true
        },

        {
          name: 'deliveryTo.city',
          label: 'City',
          type: 'select',
          options: HARDCODED_SHOPPER_DELIVERY_CITY_OPTIONS,
          grid: { xs: 12, sm: 6 },
          required: true,
          fullWidth: true,
          readonly: true
        }
      ]
    },

    {
      title: 'Delivery Details',
      alignment: 'left',
      fields: [
        {
          name: 'timePeriod',
          label: 'How long can you wait?',
          type: 'select',
          options: [
            { value: '1 week', label: '1 Week' },
            { value: '2 weeks', label: '2 Weeks' },
            { value: '3 weeks', label: '3 Weeks' },
            { value: 'Anytime', label: 'Anytime' }
          ],
          grid: { xs: 12, sm: 6 },
          required: true,
          fullWidth: true
        }
      ]
    },

    {
      title: 'Delivery Instructions',
      alignment: 'left',
      fields: [
        {
          label: '',
          name: 'deliveryInstructions',
          type: 'textarea',
          rows: 4,
          grid: { xs: 12 },
          placeholder: 'Enter the address for meet up location',
          fullWidth: true,
          preventAutoFocus: true
        }
      ]
    }
  ]

  if (flightItinerary) {
    formSections = formSections.filter((section) => section.title !== 'Delivering From')
    formSections = formSections.filter((section) => section.title !== 'Delivery Details')
  }

  const handleSubmit = (values: unknown) => {
    const deliveryValues = values as DeliveryDetailsValues
    // eslint-disable-next-line no-console
    console.log(deliveryValues)
    // TODO: Handle form submission
  }

  const handleFormChange = (formikProps: Partial<FormikProps<unknown>>) => {
    if (formikProps.values && formikProps.isValid !== undefined) {
      const values = formikProps.values as DeliveryDetailsValues
      onFormChange(values, formikProps.isValid)
    }
  }

  return (
    <Card sx={styles.container}>
      <CardContent>
        <Typography variant="h5" sx={styles.title}>
          Recipient Details
        </Typography>

        <Alert severity="warning" sx={styles.warningBox}>
          <Typography sx={styles.warningText}>Please ensure the details entered below are correct.</Typography>
        </Alert>

        <Form
          key={profileData ? 'profile-loaded' : 'profile-loading'}
          hideButtons={true}
          buttonText=""
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          formSections={formSections}
          onFormChange={handleFormChange}
        />

        {showDiscountCode && (
          <Box sx={{ mt: 2 }}>
            <DiscountCode
              value={discountCode || ''}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                if (onDiscountCodeChange) {
                  onDiscountCodeChange(event.target.value)
                }
              }}
              orderTotal={orderTotal}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

export default DeliveryDetails
